/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import { getFiles, getFilePath } from './api/files'
import { getLinkedEntities, getSiteProperties, getSiteFolderEmailAddress, getSiteUploadFolderId } from './api/etmf'
import type {
  IStagingService,
  StagingInfo,
  StagingFolderInfo,
  StagingUploadOptions,
  StagingLevel
} from '../types/staging'
import checkmateService from './checkmate'

/**
 * StagingService - React TypeScript equivalent of AngularJS stagingService
 *
 * Manages staging context (site, study, environment, property search) and provides
 * staging-related operations like folder info retrieval, document upload, and
 * checkmate action emission.
 */
class StagingService implements IStagingService {
  private static instance: StagingService

  // Context IDs
  private siteId: string | number | null = null
  private studyId: string | number | null = null
  private envId: string | number | null = null
  private propSearchId: string | number | null = null

  // Caches
  private cachedSites: Record<string, StagingInfo> = {}
  private cachedFolders: Record<string, StagingFolderInfo> = {}
  private cachedUploadFolders: Record<string, string | number> = {}

  public readonly LEVEL = {
    site: 'site',
    study: 'study',
    environment: 'environment',
    propertySearch: 'propertySearch'
  }

  private constructor() {
    // Initialize from localStorage if available
    this.siteId = this.getLocalStorageData('siteId')
    this.studyId = this.getLocalStorageData('studyId')
    this.envId = this.getLocalStorageData('environmentId')
    this.propSearchId = this.getLocalStorageData('propertySearchId')
  }

  public static getInstance(): StagingService {
    if (!StagingService.instance) {
      StagingService.instance = new StagingService()
    }
    return StagingService.instance
  }

  // Context ID getters
  getSiteId(): string | number | null {
    return this.siteId
  }

  getStudyId(): string | number | null {
    return this.studyId
  }

  getEnvironmentId(): string | number | null {
    return this.envId
  }

  getPropertySearchId(): string | number | null {
    return this.propSearchId
  }

  getContextId(): string | number | null {
    if (this.siteId || this.getLocalStorageData('siteId')) {
      return this.siteId || this.getLocalStorageData('siteId')
    } else if (this.studyId || this.getLocalStorageData('studyId')) {
      return this.studyId || this.getLocalStorageData('studyId')
    } else if (this.envId || this.getLocalStorageData('environmentId')) {
      return this.envId || this.getLocalStorageData('environmentId')
    } else if (this.propSearchId || this.getLocalStorageData('propertySearchId')) {
      return this.propSearchId || this.getLocalStorageData('propertySearchId')
    }
    return null
  }

  // Context ID setters
  setSiteId(id: string | number): void {
    if (id !== undefined && id !== null) {
      console.log('setting site', id)
      this.setLocalStorageData('siteId', id)
      this.siteId = id
    }
  }

  setStudyId(id: string | number): void {
    if (id !== undefined && id !== null) {
      console.log('setting study', id)
      this.setLocalStorageData('studyId', id)
      this.studyId = id
    }
  }

  setEnvironmentId(id: string | number): void {
    if (id !== undefined && id !== null) {
      console.log('setting environment', id)
      this.setLocalStorageData('environmentId', id)
      this.envId = id
    }
  }

  setPropertySearchId(id: string | number): void {
    if (id !== undefined && id !== null) {
      console.log('setting PropertySearch', id)
      this.setLocalStorageData('propertySearchId', id)
      this.propSearchId = id
    }
  }

  // Exit methods
  exitSite(): void {
    console.log('leaving site')
    this.removeLocalStorageData('siteId')
    this.siteId = null
  }

  exitStudy(): void {
    console.log('leaving study')
    this.removeLocalStorageData('studyId')
    this.studyId = null
  }

  exitEnvironment(): void {
    console.log('leaving environment')
    this.removeLocalStorageData('environmentId')
    this.envId = null
  }

  exitPropertySearch(): void {
    console.log('leaving PropertySearch')
    this.removeLocalStorageData('propertySearchId')
    this.propSearchId = null
  }

  // Level checking
  isLevel(level: string): boolean {
    return this.getLevel() === level
  }

  getLevel(): string | undefined {
    if (this.siteId !== null) {
      return this.LEVEL.site
    } else if (this.studyId !== null) {
      return this.LEVEL.study
    } else if (this.envId !== null) {
      return this.LEVEL.environment
    } else if (this.propSearchId !== null) {
      return this.LEVEL.propertySearch
    }
    return undefined
  }

  // Staging operations
  getStagingInfo(id: string | number): StagingInfo {
    const idStr = String(id)

    if (this.cachedSites[idStr]) {
      return this.cachedSites[idStr]
    }

    const siteInfo: StagingInfo = {}

    if (typeof id === 'string' || typeof id === 'number') {
      this.cachedSites[idStr] = siteInfo

      // Load linked entities and site properties
      getLinkedEntities()
        .then((data) => {
          const entityTypes = [
            String(data.entityLinkMap.SITE),
            String(data.entityLinkMap.TRIAL),
            String(data.entityLinkMap.SITE_ADDRESS),
            String(data.entityLinkMap.SITE_NUMBER)
          ]

          return getSiteProperties(id, entityTypes)
        })
        .then((propsData) => {
          console.log('staging info', propsData)

          // Update the cached site info
          Object.assign(siteInfo, {
            address: propsData[Object.keys(propsData)[2]], // SITE_ADDRESS
            studyName: propsData[Object.keys(propsData)[1]], // TRIAL
            siteName: propsData[Object.keys(propsData)[0]], // SITE
            siteNumber: propsData[Object.keys(propsData)[3]] // SITE_NUMBER
          })
        })
        .catch((error: any) => console.error('Error loading staging info:', error))

      // Load site folder email address
      getSiteFolderEmailAddress(id)
        .then((data) => {
          console.log('context email', data.email)
          siteInfo.folderEmailAddress = data.email
        })
        .catch((error: any) => console.error('Error loading folder email:', error))
    }

    return siteInfo
  }

  async getStagingFolderInfo(): Promise<StagingFolderInfo | undefined> {
    const contextId = this.getContextId()

    if (!contextId) {
      return undefined
    }

    const contextIdStr = String(contextId)

    if (this.cachedFolders[contextIdStr]) {
      return this.cachedFolders[contextIdStr]
    }

    try {
      const [filesData, pathData] = await Promise.all([getFiles(Number(contextId)), getFilePath(Number(contextId))])

      let folderInfo: StagingFolderInfo = {
        ...(filesData as any) // FilesResponse structure
      }

      if (Array.isArray(pathData) && pathData.length) {
        const lastPathItem = pathData.pop()
        folderInfo = { ...folderInfo, ...lastPathItem, path: pathData }
      }

      console.log('staging folder info', folderInfo)
      this.cachedFolders[contextIdStr] = folderInfo

      return folderInfo
    } catch (error) {
      console.error('Error loading staging folder info:', error)
      return undefined
    }
  }

  async getStagingUploadFolderId(): Promise<string | number | undefined> {
    const contextId = this.getContextId()

    if (!contextId) {
      return undefined
    }

    const contextIdStr = String(contextId)

    if (this.cachedUploadFolders[contextIdStr]) {
      return this.cachedUploadFolders[contextIdStr]
    }

    try {
      const data = await getSiteUploadFolderId(contextId)
      console.log('staging upload folder id', data)

      this.cachedUploadFolders[contextIdStr] = data.id
      return data.id
    } catch (error) {
      console.error('Error loading staging upload folder ID:', error)
      return undefined
    }
  }

  async uploadStagingDocument(): Promise<any> {
    try {
      const [folderInfo, folderId] = await Promise.all([this.getStagingFolderInfo(), this.getStagingUploadFolderId()])

      console.log('folderInfo', folderInfo)
      console.log('uploadFolder', folderId)

      if (!folderId || !folderInfo) {
        throw new Error('Missing folder info or upload folder ID')
      }

      // TODO: Implement modal opening when ModalsFactory is available
      // For now, return the data needed for upload
      return {
        folderId,
        options: {
          isSiteUpload: true,
          parentFolderType: folderInfo.systemFolderType,
          templateId: folderInfo.templateId
        } as StagingUploadOptions
      }
    } catch (error) {
      console.error('Error uploading staging document:', error)
      throw error
    }
  }

  async emitCheckmateAction(): Promise<any> {
    const contextId = this.getContextId()

    try {
      console.log('ye wala staging ke liye emitting staging checkmate action')
      const folder = await this.getStagingFolderInfo()
      if (contextId && folder) {
        console.log('if ke andar staging ke liye emitting staging checkmate action')
        await checkmateService.emitCustomCheckmateAction(folder, 'staging-stagingArea')
      } else {
        console.log('else ke andar staging ke liye emitting staging checkmate action')
        await checkmateService.emitDefaultCheckmateAction('staging-stagingArea')
      }
    } catch (error) {
      await checkmateService.emitDefaultCheckmateAction('staging-stagingArea')
      console.log('Emitting default checkmate action due to error:', error)
      return { type: 'default' }
    }
  }

  // Local storage helpers
  getLocalStorageData(id: string): any {
    try {
      const item = localStorage.getItem(id)
      return item ? JSON.parse(item) : null
    } catch (error) {
      console.error('Error reading from localStorage:', error)
      return null
    }
  }

  setLocalStorageData(id: string, value: any): void {
    try {
      localStorage.setItem(id, JSON.stringify(value))
    } catch (error) {
      console.error('Error writing to localStorage:', error)
    }
  }

  removeLocalStorageData(id: string): void {
    try {
      localStorage.removeItem(id)
    } catch (error) {
      console.error('Error removing from localStorage:', error)
    }
  }
}

export const stagingService = StagingService.getInstance()
